import type { DropDownNoSelection, DropDownResult, JsonData } from '@myosh/odin-components';
import { Button, Dialog, isDropDownNoSelection } from '@myosh/odin-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExtendedDynamicFormWorkflowStep, RecordLinkType } from '../../../../@types/forms';
import type { ViewConfig } from '../../../../@types/views';
import useViewConfigs from '../../../../hooks/use-view-configs';
import { useFormSettingsQuery } from '../../../../redux/services/api';
import {
  defaultLastEditedFilterOption,
  defaultRecordFilterOption,
  lastEditedFilterOptions,
  recordFilterOptions,
  resolveLastEditedFilterConfigValue,
  resolveRecordFilterConfigValue,
} from '../../../../utils/dynamic-page-utils';
import { forceAssert } from '../../../../utils/type-guards';
import OdinDropDown from '../../custom-drop-downs/odin-drop-down.component';
import LinkedRecordPreview from './linked-record-preview';
import LinkedRecordsSelectingGrid from './selection-modal/linked-records-selecting-grid';
import MobileLinkedRecordsSelecting from './selection-modal/linked-records-selecting-mobile';

interface LinkedRecordsSelectingProps {
  recordId: number | undefined;
  title?: string;
  visible: boolean;
  hidden: () => void;
  applyCallback: (linkedReocrdIds: Array<string>) => void;
  sourceModuleFormId?: number;
  targetModuleFormId?: number;
  recordLinkType?: RecordLinkType;
  value: Array<string>;
  linkedWorkflowSteps?: Array<ExtendedDynamicFormWorkflowStep>;
  isMobile?: boolean;
}

export default function LinkedRecordsSelecting({
  recordId,
  title,
  visible,
  hidden,
  applyCallback,
  sourceModuleFormId,
  targetModuleFormId,
  recordLinkType,
  value,
  linkedWorkflowSteps,
  isMobile = false,
}: LinkedRecordsSelectingProps) {
  const linkedRecordsSelected = useRef<Array<string>>(value || []);
  const [selectedConfig, setSelectedConfig] = useState<DropDownResult | DropDownNoSelection>();
  const [selectedLastEditedFilter, setSelectedLastEditedFilter] =
    useState<DropDownResult>(defaultLastEditedFilterOption);
  const [selectedRecordsFilter, setSelectedRecordsFilter] = useState<DropDownResult>(defaultRecordFilterOption);
  const [previewRecordId, setPreviewRecordId] = useState<number>();

  const { t } = useTranslation();

  const isCloneRecordLinkType = recordLinkType === 'CLONE';
  const formId = isCloneRecordLinkType ? sourceModuleFormId : targetModuleFormId;

  const { data: viewConfigs } = useViewConfigs(formId);
  const { data: formSettings } = useFormSettingsQuery(formId!, { skip: formId === undefined || formId <= 0 });

  useEffect(() => {
    setSelectedLastEditedFilter(resolveLastEditedFilterConfigValue(formSettings?.dateRange));
  }, [formSettings]);

  const updatePreviewRecord = useCallback((recordId?: number) => {
    setPreviewRecordId(recordId);
  }, []);

  const onConfigChange = (config?: DropDownResult | Array<DropDownResult> | DropDownNoSelection) => {
    if (config && !Array.isArray(config)) {
      setSelectedConfig(config);

      if (!isDropDownNoSelection(config)) {
        const viewConfig = viewConfigs?.find((item: ViewConfig) => item.id === config.value);
        const selectedRecordsFilter = resolveRecordFilterConfigValue(viewConfig?.archivedType);
        const selectedLastEditedFilter = resolveLastEditedFilterConfigValue(viewConfig?.dateRange);

        setSelectedRecordsFilter(selectedRecordsFilter);
        setSelectedLastEditedFilter(selectedLastEditedFilter);
      }
    }
  };

  const onRecordsFilterChange = (value?: DropDownResult | Array<DropDownResult> | DropDownNoSelection) => {
    if (value) {
      if (isDropDownNoSelection(value)) {
        setSelectedRecordsFilter(defaultRecordFilterOption);
      } else if (!Array.isArray(value)) {
        setSelectedRecordsFilter(value);
      } else {
        setSelectedRecordsFilter(value[0]);
      }
    }
  };

  const onLastEditedFilterChange = (value?: DropDownResult | Array<DropDownResult> | DropDownNoSelection) => {
    if (value) {
      if (isDropDownNoSelection(value)) {
        setSelectedLastEditedFilter(defaultLastEditedFilterOption);
      } else if (!Array.isArray(value)) {
        setSelectedLastEditedFilter(value);
      } else {
        setSelectedLastEditedFilter(value[0]);
      }
    }
  };

  const handleApplyChanges = () => {
    applyCallback(linkedRecordsSelected.current);
    hidden();
  };

  const handleClosePreview = () => setPreviewRecordId(undefined);

  const configDropdownStyle = 'sm:mb-5 flex flex-shrink-0 flex-row items-center transition-transform';

  const header = (
    <div className="custom-scroll flex flex-col items-start overflow-x-auto max-sm:grow max-sm:pl-2 sm:pl-9">
      {title && <div className="mb-2">{title}</div>}
      <div className="mt-2 flex gap-3">
        <OdinDropDown
          textField="name"
          valueField="id"
          data={forceAssert<JsonData>(viewConfigs)}
          value={selectedConfig}
          allowSearch={false}
          allowClear={false}
          setDefaultValue={true}
          className={configDropdownStyle}
          label={t('view')}
          size="md"
          onChange={onConfigChange}
        />
        <OdinDropDown
          textField="text"
          valueField="value"
          data={lastEditedFilterOptions}
          allowSearch={false}
          allowClear={false}
          className={configDropdownStyle}
          label={t('last-edited')}
          size="md"
          onChange={onLastEditedFilterChange}
          value={selectedLastEditedFilter}
        />
        <OdinDropDown
          textField="text"
          valueField="value"
          data={recordFilterOptions}
          allowSearch={false}
          allowClear={false}
          className={configDropdownStyle}
          label={t('show-records')}
          size="md"
          onChange={onRecordsFilterChange}
          value={selectedRecordsFilter}
        />
      </div>
    </div>
  );

  const footer = useMemo(
    () => (
      <div className="flex justify-start">
        <Button type="primary" variant="alternative" onClick={handleApplyChanges}>
          {isCloneRecordLinkType ? t('clone-records') : t('apply')}
        </Button>
      </div>
    ),
    [handleApplyChanges]
  );

  const setSelectedRecords = useCallback((records: Array<string>) => {
    linkedRecordsSelected.current = records;
  }, []);

  return (
    <Dialog
      header={header}
      footer={footer}
      visible={visible}
      onClose={hidden}
      fullScreen={true}
      contentClassName="!py-0"
    >
      <>
        {!isMobile && (
          <LinkedRecordsSelectingGrid
            recordId={recordId}
            sourceModuleFormId={sourceModuleFormId}
            targetModuleFormId={targetModuleFormId}
            recordLinkType={recordLinkType}
            selectedConfig={selectedConfig}
            selectedLastEditedFilter={selectedLastEditedFilter}
            selectedRecordsFilter={selectedRecordsFilter}
            updatePreviewRecord={updatePreviewRecord}
            formSettings={formSettings}
            value={value}
            linkedWorkflowSteps={linkedWorkflowSteps}
            setSelectedRecords={setSelectedRecords}
            previewRecordId={previewRecordId ? String(previewRecordId) : undefined}
          />
        )}
        {isMobile && (
          <MobileLinkedRecordsSelecting
            recordId={recordId}
            formId={formId}
            selectedConfig={selectedConfig}
            selectedLastEditedFilter={selectedLastEditedFilter}
            selectedRecordsFilter={selectedRecordsFilter}
            updatePreviewRecord={updatePreviewRecord}
            formSettings={formSettings}
            value={value}
            linkedWorkflowSteps={linkedWorkflowSteps}
            setSelectedRecords={setSelectedRecords}
          />
        )}
        {previewRecordId !== undefined && (
          <LinkedRecordPreview recordId={String(previewRecordId)} onClose={handleClosePreview} />
        )}
      </>
    </Dialog>
  );
}
